import { ArrowRight, ChevronRight } from 'lucide-react';
import { useState } from 'react';

import { httpOutcome, receiveChunks, wireResponse } from './collabboard-models';
import { Lab, Panel, Reset, Result } from './lab-parts';

const streamText = 'GET_TASKS\nCREATE_TASK|Learn React\n';
const visible = (text: string) => text.replaceAll('\n', '⏎');

export function StreamLab() {
  const [chunks, setChunks] = useState<string[]>([]);
  const [size, setSize] = useState('5');
  const [buffered, setBuffered] = useState(false);
  const offset = chunks.join('').length;
  const result = receiveChunks(chunks, buffered);
  return (
    <Lab
      title="A stream does not know your message boundaries"
      eyebrow="04 / Two processes"
      note="Each chunk contains bytes returned by a read. Use the protocol to find the message boundary."
    >
      <div className="cb-controls">
        <label>
          Bytes per receive
          <select
            value={size}
            onChange={(e) => {
              setSize(e.target.value);
              setChunks([]);
            }}
          >
            <option value="5">5 bytes</option>
            <option value="1">1 byte</option>
            <option value="100">Everything at once</option>
          </select>
        </label>
        <label>
          Receiver rule
          <select
            value={buffered ? 'buffer' : 'one'}
            onChange={(e) => {
              setBuffered(e.target.value === 'buffer');
              setChunks([]);
            }}
          >
            <option value="one">One receive = one message</option>
            <option value="buffer">Buffer until newline</option>
          </select>
        </label>
      </div>
      <div className="cb-flow-head">
        <span>Client process</span>
        <ArrowRight size={17} aria-hidden="true" />
        <span>Ordered byte stream</span>
        <ArrowRight size={17} aria-hidden="true" />
        <span>Server process</span>
      </div>
      <div className="cb-stream" aria-label="Sent bytes">
        <code className="cb-delivered">{visible(streamText.slice(0, offset))}</code>
        <code>{visible(streamText.slice(offset))}</code>
      </div>
      <div className="cb-two">
        <Panel label="recv() returned">
          <div className="cb-chunks">
            {chunks.length ? (
              chunks.map((chunk, index) => <code key={index}>{visible(chunk)}</code>)
            ) : (
              <span>Waiting for the first receive</span>
            )}
          </div>
        </Panel>
        <Panel label="Messages the application would receive">
          <ol className="cb-message-list">
            {result.messages.map((message, index) => (
              <li key={index}>
                <code>{visible(message) || '(empty)'}</code>
              </li>
            ))}
          </ol>
          {result.messages.length === 0 && <p>No complete messages yet</p>}
          <div className="cb-buffer">
            <small>Unfinished buffer</small>
            <code>{visible(result.pending) || 'empty'}</code>
          </div>
        </Panel>
      </div>
      <div className="cb-controls">
        <button
          className="cb-action"
          disabled={offset >= streamText.length}
          onClick={() => setChunks([...chunks, streamText.slice(offset, offset + Number(size))])}
        >
          Deliver next chunk
        </button>
        <button
          disabled={offset >= streamText.length}
          onClick={() => {
            const remaining: string[] = [];
            for (let i = offset; i < streamText.length; i += Number(size))
              remaining.push(streamText.slice(i, i + Number(size)));
            setChunks([...chunks, ...remaining]);
          }}
        >
          Deliver remaining bytes
        </button>
        <Reset onClick={() => setChunks([])} />
      </div>
      <Result tone={offset === 0 ? 'neutral' : buffered ? 'success' : 'warning'}>
        {offset === 0
          ? 'The client sends two newline-terminated commands. Predict what the server will treat as a command.'
          : buffered
            ? `${result.messages.length} complete command${result.messages.length === 1 ? '' : 's'} found. Incomplete bytes wait for a newline; changing chunk size does not change the final commands.`
            : 'The receiver treats transport chunks as commands. A chunk can be half a command—or two commands together.'}
      </Result>
    </Lab>
  );
}

export function HttpLab() {
  const [method, setMethod] = useState('GET');
  const [path, setPath] = useState('/tasks');
  const [title, setTitle] = useState('Learn HTTP');
  const [malformed, setMalformed] = useState(false);
  const [response, setResponse] = useState<ReturnType<typeof httpOutcome> | null>(null);
  const [part, setPart] = useState<'line' | 'head' | 'body'>('line');
  const body = method === 'POST' ? (malformed ? '{title:' : JSON.stringify({ title })) : '';
  const bytes = new TextEncoder().encode(body).length;
  const notes = {
    line: 'Method + request target + version. The port chooses a server; the path chooses a resource inside that server.',
    head: 'Headers describe the message. Content-Length counts encoded body bytes, not characters; try a title containing é.',
    body: 'The empty line ends the headers. A body carries data for the operation; valid JSON still needs application validation.',
  };
  return (
    <Lab
      title="Read the conversation, not just the status"
      eyebrow="05 / HTTP messages"
      note="Each send starts with one saved task. Compare the request with the resulting response."
    >
      <div className="cb-controls">
        <label>
          Method
          <select
            value={method}
            onChange={(e) => {
              setMethod(e.target.value);
              setResponse(null);
            }}
          >
            <option>GET</option>
            <option>POST</option>
            <option>DELETE</option>
          </select>
        </label>
        <label>
          Path
          <select
            value={path}
            onChange={(e) => {
              setPath(e.target.value);
              setResponse(null);
            }}
          >
            <option>/tasks</option>
            <option>/missing</option>
          </select>
        </label>
        {method === 'POST' && (
          <label>
            Task title
            <input
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setResponse(null);
              }}
            />
          </label>
        )}
      </div>
      {method === 'POST' && (
        <label className="cb-check">
          <input
            type="checkbox"
            checked={malformed}
            onChange={(e) => {
              setMalformed(e.target.checked);
              setResponse(null);
            }}
          />
          Send malformed JSON
        </label>
      )}
      <div className="cb-two">
        <Panel label="Request →">
          <div className="cb-wire">
            <button aria-pressed={part === 'line'} onClick={() => setPart('line')}>
              <code>
                {method} {path} HTTP/1.1
              </code>
            </button>
            <button aria-pressed={part === 'head'} onClick={() => setPart('head')}>
              <code>
                Host: 127.0.0.1:8000
                <br />
                Accept: application/json
                {method === 'POST' && (
                  <>
                    <br />
                    Content-Type: application/json
                    <br />
                    Content-Length: {bytes}
                  </>
                )}
              </code>
            </button>
            <button aria-pressed={part === 'body'} onClick={() => setPart('body')}>
              <small>↵ empty line</small>
              <code>{body || '(no body)'}</code>
            </button>
          </div>
        </Panel>
        <Panel label="← Response">
          <pre className="cb-response" aria-label="HTTP response">
            {response ? wireResponse(response) : 'Send the request to inspect its response.'}
          </pre>
          {response && <span className="cb-state-total">Saved tasks: 1 → {response.count}</span>}
        </Panel>
      </div>
      <p className="cb-inspector-note">{notes[part]}</p>
      <div className="cb-controls">
        <button className="cb-action" onClick={() => setResponse(httpOutcome(method, path, body))}>
          Send request
        </button>
        <Reset
          onClick={() => {
            setMethod('GET');
            setPath('/tasks');
            setTitle('Learn HTTP');
            setMalformed(false);
            setResponse(null);
            setPart('line');
          }}
        />
      </div>
      <Result tone={response ? (response.status < 400 ? 'success' : 'warning') : 'neutral'}>
        {response
          ? `${response.status} ${response.reason} · ${response.decision}`
          : 'Inspect each part of the request. Then send it and compare the status, body and saved state.'}
      </Result>
    </Lab>
  );
}

const pipelineCases = {
  create: {
    request: 'POST /tasks  {"title":"Learn HTTP"}',
    status: '201 Created',
    note: 'A valid request reaches your task operation; save before reporting success.',
  },
  blank: {
    request: 'POST /tasks  {"title":"   "}',
    status: '422 Unprocessable Content',
    note: 'Your declared title validator rejects whitespace. The task operation never runs.',
  },
  missing: {
    request: 'GET /tasks/999',
    status: '404 Not Found',
    note: '999 is a valid integer. Only the application lookup can decide that this task does not exist.',
  },
};
type PipelineCase = keyof typeof pipelineCases;
const manualStages = [
  'accept + read',
  'method/path dispatch',
  'parse + check',
  'task operation',
  'encode + send',
];
const frameworkStages = [
  'Uvicorn',
  'FastAPI router',
  'Pydantic model',
  'Your handler',
  'JSON response',
];
const stageDescriptions = [
  'Receive a complete HTTP request.',
  'Choose the operation from the method and path.',
  'Check the declared input shape and rules.',
  'Apply the task rule and save accepted changes.',
  'Send the result with an explicit status and body.',
];

export function FrameworkLab() {
  const [framework, setFramework] = useState(true);
  const [scenario, setScenario] = useState<PipelineCase>('create');
  const [step, setStep] = useState(-1);
  const current = pipelineCases[scenario];
  const route = scenario === 'blank' ? [0, 1, 2, 4] : [0, 1, 2, 3, 4];
  const currentStage = route[step] ?? -1;
  const done = step >= route.length - 1;
  return (
    <Lab title="Who owns this part of the request?" eyebrow="06 / Framework boundaries">
      <div className="cb-tabs" aria-label="Implementation">
        <button aria-pressed={!framework} onClick={() => setFramework(false)}>
          Our socket server
        </button>
        <button aria-pressed={framework} onClick={() => setFramework(true)}>
          Uvicorn + FastAPI
        </button>
      </div>
      <div className="cb-controls">
        <label>
          Request case
          <select
            value={scenario}
            onChange={(e) => {
              setScenario(e.target.value as PipelineCase);
              setStep(-1);
            }}
          >
            <option value="create">Create a valid task</option>
            <option value="blank">Create with a blank title</option>
            <option value="missing">Read a missing task</option>
          </select>
        </label>
      </div>
      <code className="cb-code-line">{current.request}</code>
      <ol className="cb-pipeline">
        {(framework ? frameworkStages : manualStages).map((stage, index) => (
          <li
            key={stage}
            data-reached={route.slice(0, step + 1).includes(index)}
            data-current={currentStage === index}
            data-blocked={scenario === 'blank' && index === 3 && currentStage >= 2}
          >
            <span>{index + 1}</span>
            <strong>{stage}</strong>
            <small>
              {scenario === 'blank' && index === 3 && currentStage >= 2
                ? 'Skipped: input rejected'
                : stageDescriptions[index]}
            </small>
            {index < 4 && <ChevronRight size={15} aria-hidden="true" />}
          </li>
        ))}
      </ol>
      <div className="cb-two cb-pipeline-result">
        <Panel label="Application state">
          <strong>Tasks: {scenario === 'create' && step >= 3 ? '1 → 2' : '1 (unchanged)'}</strong>
          <small>
            {step < 3 || scenario !== 'create' ? 'No change saved' : 'Saved by your task operation'}
          </small>
        </Panel>
        <Panel label="Client receives">
          <strong>
            {done
              ? scenario === 'blank' && !framework
                ? '400 Bad Request'
                : current.status
              : 'Waiting for a result'}
          </strong>
          <small>
            {done
              ? 'An HTTP response, including for expected rejections'
              : 'A connection is not a completed operation'}
          </small>
        </Panel>
      </div>
      <div className="cb-controls">
        <button className="cb-action" disabled={done} onClick={() => setStep(step + 1)}>
          Advance request
        </button>
        <Reset onClick={() => setStep(-1)} />
      </div>
      <Result tone={done ? (scenario === 'create' ? 'success' : 'warning') : 'neutral'}>
        {done
          ? current.note
          : step < 0
            ? 'Advance the request, then change the implementation view. The job still exists when a framework does it for you.'
            : stageDescriptions[currentStage]}
      </Result>
    </Lab>
  );
}
